/** @jsx jsx */
/** @jsxFrag React.Fragment */
import React from 'react';

import { css, jsx } from '@compiled/react';
import { useIntl } from 'react-intl';
import { token } from '@atlaskit/tokens';
import { IconButton } from '@atlaskit/button/new';
import CustomizeIcon from '@atlaskit/icon/core/customize';
import { ButtonItem, MenuGroup, Section } from '@atlaskit/menu';
import Popup from '@atlaskit/popup';
import type { TriggerProps } from '@atlaskit/popup';

import { useSetCustomiseSidebarTrigger } from 'view/common/pushing-customize-sidebar';
import { useMediaQuery } from 'view/help-center/use-screen-size/use-screen-size';

import hubMessages from '../messages';

export interface HubCustomizeMenuItem {
	label: string;
	testId: string;
	onClick: () => void;
}

interface HubCustomizeMenuProps {
	items: HubCustomizeMenuItem[];
	iconColor?: string;
}

const menuContainerStyles = css({
	width: 234,
	backgroundColor: token('elevation.surface', '#FFFFFF'),
	borderRadius: token('radius.small', '3px'),
	boxShadow: token('elevation.shadow.overlay', '0 8px 12px #091e4226'),
});

const setRefValue = (ref: TriggerProps['ref'], element: HTMLButtonElement | null) => {
	if (typeof ref === 'function') {
		ref(element);
	} else if (ref && typeof ref === 'object' && 'current' in ref) {
		(ref as React.MutableRefObject<HTMLElement | null>).current = element;
	}
};

const HubCustomizeMenuComponent = ({ items, iconColor }: HubCustomizeMenuProps) => {
	const [isOpen, setIsOpen] = React.useState(false);
	const { formatMessage } = useIntl();
	const isMobile = useMediaQuery('(max-width: 768px)');
	const setCustomiseSidebarTrigger = useSetCustomiseSidebarTrigger();

	const handleToggle = React.useCallback(() => {
		setIsOpen((prev) => !prev);
	}, []);

	const handleItemClick = React.useCallback((onClick: () => void) => {
		onClick();
		setIsOpen(false);
	}, []);

	const renderContent = React.useCallback(
		() => (
			<div data-testid="hub-customize-menu-content" css={menuContainerStyles}>
				<MenuGroup spacing="cozy">
					<Section>
						{items.map((item) => (
							<ButtonItem
								key={item.testId}
								testId={item.testId}
								onClick={() => handleItemClick(item.onClick)}
							>
								{item.label}
							</ButtonItem>
						))}
					</Section>
				</MenuGroup>
			</div>
		),
		[items, handleItemClick],
	);

	const renderTrigger = React.useCallback(
		({ ref, ...triggerProps }: TriggerProps) => {
			const combinedRef = (element: HTMLButtonElement | null) => {
				setRefValue(ref, element);
				setCustomiseSidebarTrigger?.(element);
			};

			return (
				<IconButton
					{...triggerProps}
					ref={combinedRef}
					appearance="subtle"
					icon={(iconProps) =>
						iconColor ? (
							<span data-testid="hub-customize-menu-icon" style={{ color: iconColor }}>
								<CustomizeIcon {...iconProps} />
							</span>
						) : (
							<CustomizeIcon {...iconProps} />
						)
					}
					label={formatMessage(hubMessages.customizeMenuLabel)}
					testId="hub-customize-menu-trigger"
					onClick={handleToggle}
				/>
			);
		},
		[formatMessage, handleToggle, iconColor, setCustomiseSidebarTrigger],
	);

	if (items.length === 0 || isMobile) {
		return null;
	}

	return (
		<Popup
			isOpen={isOpen}
			onClose={() => setIsOpen(false)}
			placement="bottom-end"
			shouldRenderToParent
			content={renderContent}
			trigger={renderTrigger}
		/>
	);
};

HubCustomizeMenuComponent.displayName = 'HubCustomizeMenu';

export const HubCustomizeMenu = React.memo(HubCustomizeMenuComponent);
